import type { Suggestion } from '@/components/ui/AutocompleteInput';
import { db } from '@/lib/firebase';
import { collection, query as firestoreQuery, where, getDocs, limit, orderBy } from 'firebase/firestore';
import { COUNTRY_LIST as PROFILE_COUNTRY_LIST } from '@/data/profileOptions';

/**
 * Common merchant/place names for expense suggestions
 */
const COMMON_MERCHANTS = [
  'Starbucks', 'McDonald\'s', 'Subway', 'KFC', 'Pizza Hut', 'Domino\'s',
  'Uber', 'Lyft', 'Taxi', 'Airport Taxi', 'Train Station', 'Bus Station',
  'Amazon', 'Walmart', 'Target', 'CVS', 'Walgreens', '7-Eleven',
  'Hilton', 'Marriott', 'Airbnb', 'Booking.com', 'Expedia',
  'United Airlines', 'Delta', 'American Airlines', 'Southwest',
  'Shell', 'BP', 'Exxon', 'Chevron',
];

/**
 * Common expense descriptions
 */
const COMMON_DESCRIPTIONS = [
  'Flight ticket', 'Hotel booking', 'Restaurant meal', 'Coffee',
  'Taxi ride', 'Uber ride', 'Train ticket', 'Bus ticket',
  'Museum entry', 'Tour guide', 'Souvenir', 'Shopping',
  'Breakfast', 'Lunch', 'Dinner', 'Snacks', 'Drinks',
  'Gas', 'Parking', 'Toll', 'Car rental',
];

/**
 * Common trip name patterns
 */
const COMMON_TRIP_PATTERNS = [
  'Summer Vacation', 'Weekend Getaway', 'Business Trip', 'Family Vacation',
  'Honeymoon', 'Anniversary Trip', 'Birthday Trip', 'Graduation Trip',
  'Solo Adventure', 'Group Trip', 'Road Trip', 'Beach Vacation',
  'City Break', 'Mountain Retreat', 'Cultural Tour', 'Food Tour',
];

/**
 * US States list for autocomplete
 */
const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado',
  'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho',
  'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana',
  'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota',
  'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada',
  'New Hampshire', 'New Jersey', 'New Mexico', 'New York',
  'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon',
  'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington',
  'West Virginia', 'Wisconsin', 'Wyoming', 'District of Columbia',
];

/**
 * Country list for autocomplete - using full country list from profileOptions
 */
export const COUNTRIES: Suggestion[] = PROFILE_COUNTRY_LIST.map(country => ({
  value: country,
  label: country,
  icon: 'flag',
}));

/**
 * Get merchant suggestions from user's previous expenses
 */
export async function getMerchantSuggestions(
  userId: string,
  tripId?: string,
  query?: string
): Promise<Suggestion[]> {
  try {
    const suggestions: Suggestion[] = [];
    
    // Add common merchants first
    COMMON_MERCHANTS.forEach(merchant => {
      suggestions.push({
        value: merchant,
        label: merchant,
        icon: 'storefront-outline',
      });
    });

    // Try to get from Firestore if userId is provided
    if (userId) {
      try {
        let expensesQuery;
        
        if (tripId) {
          // Get merchants from this trip
          expensesQuery = firestoreQuery(
            collection(db, 'trips', tripId, 'expenses'),
            where('merchant', '!=', ''),
            orderBy('merchant'),
            limit(20)
          );
        } else {
          // Get merchants from all user's trips (would need a different structure)
          // For now, skip this if no tripId
          return suggestions;
        }

        const snapshot = await getDocs(expensesQuery);
        const merchants = new Set<string>();
        
        snapshot.forEach((docSnap) => {
          const data = docSnap.data() as Record<string, unknown>;
          if (data.merchant && typeof data.merchant === 'string') {
            merchants.add(data.merchant);
          }
        });

        // Add unique merchants from user's history
        merchants.forEach(merchant => {
          if (!COMMON_MERCHANTS.includes(merchant)) {
            suggestions.push({
              value: merchant,
              label: merchant,
              icon: 'time-outline',
              subtitle: 'From your history',
            });
          }
        });
      } catch (error) {
        console.error('[formSuggestions] Error fetching merchant suggestions:', error);
        // Continue with common merchants if Firestore fails
      }
    }

    return suggestions;
  } catch (error) {
    console.error('[formSuggestions] Error getting merchant suggestions:', error);
    return COMMON_MERCHANTS.map(merchant => ({
      value: merchant,
      label: merchant,
      icon: 'storefront-outline',
    }));
  }
}

/**
 * Get description suggestions
 */
export function getDescriptionSuggestions(query?: string): Suggestion[] {
  return COMMON_DESCRIPTIONS.map(desc => ({
    value: desc,
    label: desc,
    icon: 'receipt-outline',
  }));
}

/**
 * Get trip name suggestions
 */
export function getTripNameSuggestions(query?: string): Suggestion[] {
  return COMMON_TRIP_PATTERNS.map(pattern => ({
    value: pattern,
    label: pattern,
    icon: 'airplane-outline',
  }));
}

/**
 * Get country suggestions
 */
export function getCountrySuggestions(query?: string): Suggestion[] {
  if (!query || query.trim().length < 1) {
    return COUNTRIES.slice(0, 10); // Show top 10 by default
  }
  return COUNTRIES;
}

/**
 * Get US state suggestions
 */
export function getStateSuggestions(query?: string): Suggestion[] {
  const states: Suggestion[] = US_STATES.map(state => ({
    value: state,
    label: state,
    icon: 'location',
    subtitle: 'United States',
  }));

  if (!query || query.trim().length < 1) {
    return states.slice(0, 10); // Show top 10 by default
  }
  return states;
}

/**
 * Get city or country suggestions for place of birth
 * Combines both cities and countries
 */
export function getPlaceOfBirthSuggestions(query?: string): Suggestion[] {
  const suggestions: Suggestion[] = [];

  // Add common cities
  const commonCities = [
    'New York, USA', 'Los Angeles, USA', 'Chicago, USA', 'Houston, USA',
    'Phoenix, USA', 'Philadelphia, USA', 'San Antonio, USA', 'San Diego, USA',
    'Dallas, USA', 'San Jose, USA', 'London, UK', 'Paris, France',
    'Tokyo, Japan', 'Sydney, Australia', 'Toronto, Canada', 'Berlin, Germany',
    'Rome, Italy', 'Madrid, Spain', 'Amsterdam, Netherlands', 'Vienna, Austria',
    'Brussels, Belgium', 'Stockholm, Sweden', 'Copenhagen, Denmark', 'Oslo, Norway',
    'Helsinki, Finland', 'Dublin, Ireland', 'Lisbon, Portugal', 'Athens, Greece',
    'Warsaw, Poland', 'Prague, Czech Republic', 'Budapest, Hungary', 'Bucharest, Romania',
    'Sofia, Bulgaria', 'Zagreb, Croatia', 'Belgrade, Serbia', 'Bratislava, Slovakia',
    'Ljubljana, Slovenia', 'Moscow, Russia', 'Kiev, Ukraine', 'Minsk, Belarus',
    'Beijing, China', 'Shanghai, China', 'Hong Kong, China', 'Singapore',
    'Seoul, South Korea', 'Bangkok, Thailand', 'Manila, Philippines', 'Jakarta, Indonesia',
    'Kuala Lumpur, Malaysia', 'Hanoi, Vietnam', 'Ho Chi Minh City, Vietnam',
    'New Delhi, India', 'Mumbai, India', 'Bangalore, India', 'Chennai, India',
    'Karachi, Pakistan', 'Dhaka, Bangladesh', 'Colombo, Sri Lanka', 'Kathmandu, Nepal',
    'Dubai, UAE', 'Abu Dhabi, UAE', 'Riyadh, Saudi Arabia', 'Doha, Qatar',
    'Kuwait City, Kuwait', 'Manama, Bahrain', 'Muscat, Oman', 'Amman, Jordan',
    'Beirut, Lebanon', 'Tel Aviv, Israel', 'Jerusalem, Israel', 'Cairo, Egypt',
    'Alexandria, Egypt', 'Casablanca, Morocco', 'Tunis, Tunisia', 'Algiers, Algeria',
    'Lagos, Nigeria', 'Nairobi, Kenya', 'Addis Ababa, Ethiopia', 'Dar es Salaam, Tanzania',
    'Johannesburg, South Africa', 'Cape Town, South Africa', 'Cairo, Egypt',
    'Mexico City, Mexico', 'Guadalajara, Mexico', 'Monterrey, Mexico',
    'São Paulo, Brazil', 'Rio de Janeiro, Brazil', 'Brasília, Brazil',
    'Buenos Aires, Argentina', 'Santiago, Chile', 'Lima, Peru', 'Bogotá, Colombia',
    'Caracas, Venezuela', 'Quito, Ecuador', 'Montevideo, Uruguay', 'Asunción, Paraguay',
  ];

  commonCities.forEach(city => {
    suggestions.push({
      value: city,
      label: city,
      icon: 'location',
    });
  });

  // Add all countries
  COUNTRIES.forEach(country => {
    suggestions.push({
      value: country.value,
      label: country.value,
      icon: 'flag',
      subtitle: 'Country',
    });
  });

  if (!query || query.trim().length < 1) {
    return suggestions.slice(0, 15); // Show top 15 by default
  }
  return suggestions;
}

/**
 * Get location suggestions (cities/countries)
 */
export function getLocationSuggestions(query?: string): Suggestion[] {
  // Common travel locations
  const commonLocations: Suggestion[] = [
    { value: 'New York, USA', label: 'New York, USA', icon: 'location' },
    { value: 'Los Angeles, USA', label: 'Los Angeles, USA', icon: 'location' },
    { value: 'London, UK', label: 'London, UK', icon: 'location' },
    { value: 'Paris, France', label: 'Paris, France', icon: 'location' },
    { value: 'Tokyo, Japan', label: 'Tokyo, Japan', icon: 'location' },
    { value: 'Sydney, Australia', label: 'Sydney, Australia', icon: 'location' },
    { value: 'Rome, Italy', label: 'Rome, Italy', icon: 'location' },
    { value: 'Barcelona, Spain', label: 'Barcelona, Spain', icon: 'location' },
    { value: 'Amsterdam, Netherlands', label: 'Amsterdam, Netherlands', icon: 'location' },
    { value: 'Berlin, Germany', label: 'Berlin, Germany', icon: 'location' },
    { value: 'Dubai, UAE', label: 'Dubai, UAE', icon: 'location' },
    { value: 'Singapore', label: 'Singapore', icon: 'location' },
    { value: 'Bangkok, Thailand', label: 'Bangkok, Thailand', icon: 'location' },
    { value: 'Hong Kong', label: 'Hong Kong', icon: 'location' },
    { value: 'Toronto, Canada', label: 'Toronto, Canada', icon: 'location' },
  ];

  if (!query || query.trim().length < 1) {
    return commonLocations;
  }

  return commonLocations;
}

